import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { 
  fetchRemoteImages, 
  saveRemoteImages, 
  subscribeToLocalSync 
} from '@/lib/cloudSync';
import { 
  subscribeToFirestore, 
  saveToFirestore 
} from '@/lib/firebaseSync';

// Default system image mapping with official high-resolution assets
export const DEFAULT_IMAGES = {
  headerLogo: 'https://i.postimg.cc/c4w5ZDKc/Stellars-Estates-Logo-Upgrade1.png',
  footerLogo: 'https://i.postimg.cc/c4w5ZDKc/Stellars-Estates-Logo-Upgrade1.png',
  homeHeroBg: 'https://i.postimg.cc/3xmJCZ0S/hero-image-stellars-estates.webp',
  founderPhoto: 'https://horizons-cdn.hostinger.com/a74842f1-b47f-467e-a063-65e1f5c44287/sahil-sharma-tp493-4XV5p.webp',
  
  // Service Category Hero Defaults
  '/services/advertisement': 'https://images.unsplash.com/photo-1571786256017-aee7a0c009b6?auto=format&fit=crop&w=1200&q=80',
  '/services/marketing': 'https://images.unsplash.com/photo-1557838923-2985c318be48?auto=format&fit=crop&w=1200&q=80',
  '/services/development': 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&w=1200&q=80',
  '/services/search-engine-optimisation': 'https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=1200&q=80',
  '/services/ai-automation': 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=1200&q=80',
  '/services/designing-editing': 'https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&w=1200&q=80',
  '/services/integration': 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1200&q=80',
  '/services/consultations': 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80',
  '/services/on-site-dedicated-services': 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80',

  // Key Subservices Hero Defaults
  '/services/advertisement/online/google-ads': 'https://images.unsplash.com/photo-1571786256017-aee7a0c009b6?auto=format&fit=crop&w=1200&q=80',
  '/services/advertisement/online/meta-ads': 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?auto=format&fit=crop&w=1200&q=80',
  '/services/advertisement/online/ott-ads': 'https://images.unsplash.com/photo-1593784991095-a205069470b6?auto=format&fit=crop&w=1200&q=80',
  '/services/advertisement/offline/pvr-inox': 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=1200&q=80',
  '/services/marketing/social-media-marketing': 'https://images.unsplash.com/photo-1611746872915-64382b5c76da?auto=format&fit=crop&w=1200&q=80',
  '/services/marketing/whatsapp-marketing': 'https://images.unsplash.com/photo-1534536281715-e28d76689b4d?auto=format&fit=crop&w=1200&q=80',
  '/services/development/websites': 'https://images.unsplash.com/photo-1547658719-da2b51169166?auto=format&fit=crop&w=1200&q=80',
  '/services/development/e-commerce': 'https://images.unsplash.com/photo-1556742049-0a670f4a45a1?auto=format&fit=crop&w=1200&q=80',
  '/services/development/mobile-apps': 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1200&q=80',
  '/services/search-engine-optimisation/seo': 'https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=1200&q=80',
  '/services/ai-automation/whatsapp-ai-automation': 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=1200&q=80',
  '/services/designing-editing/creative-designing': 'https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&w=1200&q=80',
  '/services/integration/ai-integration': 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1200&q=80',
  '/services/consultations/strategy': 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80',
  '/services/on-site-dedicated-services/in-person-support': 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80',
};

const STORAGE_KEY = 'stellars_custom_images_v1';

const ImageContext = createContext();

export const ImageProvider = ({ children }) => {
  const [customImages, setCustomImages] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading custom images:', e);
    }
    return {};
  });

  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [syncStatus, setSyncStatus] = useState('connected'); // 'connected' | 'syncing' | 'offline'
  const [lastSyncedTime, setLastSyncedTime] = useState(() => new Date());
  const isInitialMount = useRef(true);

  // Helper to merge incoming remote changes safely
  const mergeRemoteImages = useCallback((remoteData) => {
    if (!remoteData || typeof remoteData !== 'object') return;
    
    // Clean metadata fields
    const cleaned = { ...remoteData };
    delete cleaned._syncedAt;
    delete cleaned._version;
    delete cleaned._updatedTimestamp;

    setCustomImages((prev) => {
      // Check if anything actually changed
      const prevKeys = Object.keys(prev);
      const newKeys = Object.keys(cleaned);
      if (prevKeys.length === newKeys.length && prevKeys.every(k => prev[k] === cleaned[k])) {
        return prev;
      }
      
      // Save locally
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
      } catch (e) {
        console.error('Error caching remote images:', e);
      }
      
      return cleaned;
    });

    setLastSyncedTime(new Date());
    setSyncStatus('connected');
  }, []);

  // 1. Initial Cloud Sync & Listeners on mount
  useEffect(() => {
    let isMounted = true;

    // Fetch latest remote overrides from Cloud
    const loadCloudData = async () => {
      setSyncStatus('syncing');
      try {
        const remoteData = await fetchRemoteImages();
        if (isMounted && remoteData) {
          mergeRemoteImages(remoteData);
        }
      } catch (err) {
        console.warn('Initial cloud image fetch failed, using local/default:', err);
      } finally {
        if (isMounted) setSyncStatus('connected');
      }
    };

    loadCloudData();

    // 2. Subscribe to local multi-tab BroadcastChannel
    const unsubscribeLocal = subscribeToLocalSync((broadcastedData) => {
      if (isMounted && broadcastedData) {
        mergeRemoteImages(broadcastedData);
      }
    });

    // 3. Subscribe to Firestore Realtime Snapshot
    const unsubscribeFirestore = subscribeToFirestore((firestoreData) => {
      if (isMounted && firestoreData) {
        mergeRemoteImages(firestoreData);
      }
    });

    // 4. Periodic background cloud refresh & focus revalidation (every 8 seconds)
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadCloudData();
      }
    }, 8000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadCloudData();
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      isMounted = false;
      if (unsubscribeLocal) unsubscribeLocal();
      if (unsubscribeFirestore && typeof unsubscribeFirestore === 'function') {
        unsubscribeFirestore();
      }
      clearInterval(intervalId);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [mergeRemoteImages]);

  // Sync to localStorage on state changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(customImages));
    } catch (e) {
      console.error('Error saving custom images:', e);
    }
  }, [customImages]);

  // Helper to get image URL with fallback
  const getImage = useCallback((key, fallbackUrl) => {
    if (customImages[key] && String(customImages[key]).trim() !== '') {
      return customImages[key];
    }
    if (DEFAULT_IMAGES[key]) {
      return DEFAULT_IMAGES[key];
    }
    return fallbackUrl || '';
  }, [customImages]);

  // Update single image key & broadcast to all devices immediately
  const setImage = useCallback(async (key, url) => {
    const updated = {
      ...customImages,
      [key]: url
    };
    
    setCustomImages(updated);
    setSyncStatus('syncing');

    try {
      // Push to Cloud Sync Relay and Firestore in parallel
      await Promise.allSettled([
        saveRemoteImages(updated),
        saveToFirestore(updated)
      ]);
      setLastSyncedTime(new Date());
      setSyncStatus('connected');
    } catch (err) {
      console.error('Failed pushing image update to cloud:', err);
      setSyncStatus('connected');
    }
  }, [customImages]);

  // Bulk update multiple image keys
  const setImageBulk = useCallback(async (newMapping) => {
    const updated = {
      ...customImages,
      ...newMapping
    };

    setCustomImages(updated);
    setSyncStatus('syncing');

    try {
      await Promise.allSettled([
        saveRemoteImages(updated),
        saveToFirestore(updated)
      ]);
      setLastSyncedTime(new Date());
      setSyncStatus('connected');
    } catch (err) {
      console.error('Failed bulk cloud push:', err);
      setSyncStatus('connected');
    }
  }, [customImages]);

  // Reset a single key
  const resetImage = useCallback(async (key) => {
    const next = { ...customImages };
    delete next[key];

    setCustomImages(next);
    setSyncStatus('syncing');

    try {
      await Promise.allSettled([
        saveRemoteImages(next),
        saveToFirestore(next)
      ]);
      setLastSyncedTime(new Date());
      setSyncStatus('connected');
    } catch (err) {
      console.error('Failed resetting image on cloud:', err);
      setSyncStatus('connected');
    }
  }, [customImages]);

  // Reset all keys to default
  const resetAllImages = useCallback(async () => {
    setCustomImages({});
    setSyncStatus('syncing');

    try {
      localStorage.removeItem(STORAGE_KEY);
      await Promise.allSettled([
        saveRemoteImages({}),
        saveToFirestore({})
      ]);
      setLastSyncedTime(new Date());
      setSyncStatus('connected');
    } catch (err) {
      console.error('Failed clearing storage on cloud:', err);
      setSyncStatus('connected');
    }
  }, []);

  // Force cloud sync now
  const forceCloudSync = useCallback(async () => {
    setSyncStatus('syncing');
    try {
      const remoteData = await fetchRemoteImages();
      if (remoteData) {
        mergeRemoteImages(remoteData);
      }
      setSyncStatus('connected');
      setLastSyncedTime(new Date());
      return true;
    } catch (e) {
      console.error('Force sync error:', e);
      setSyncStatus('connected');
      return false;
    }
  }, [mergeRemoteImages]);

  const openAdminPortal = () => {
    setIsAdminOpen(true);
  };

  const closeAdminPortal = () => {
    setIsAdminOpen(false);
  };

  return (
    <ImageContext.Provider
      value={{
        customImages,
        getImage,
        setImage,
        setImageBulk,
        resetImage,
        resetAllImages,
        forceCloudSync,
        syncStatus,
        lastSyncedTime,
        isAdminOpen,
        openAdminPortal,
        closeAdminPortal,
        isAdminAuthenticated,
        setIsAdminAuthenticated
      }}
    >
      {children}
    </ImageContext.Provider>
  );
};

const defaultContextValue = {
  customImages: {},
  getImage: (key, fallbackUrl) => DEFAULT_IMAGES[key] || fallbackUrl || '',
  setImage: () => {},
  setImageBulk: () => {},
  resetImage: () => {},
  resetAllImages: () => {},
  forceCloudSync: () => {},
  syncStatus: 'connected',
  lastSyncedTime: new Date(),
  isAdminOpen: false,
  openAdminPortal: () => {},
  closeAdminPortal: () => {},
  isAdminAuthenticated: false,
  setIsAdminAuthenticated: () => {}
};

export const useCustomImages = () => {
  const context = useContext(ImageContext);
  if (!context) {
    return defaultContextValue;
  }
  return context;
};
